
/**
 * Utility for rendering html for the onboaridng pages.
 */

import React from'react';
import { getRGB } from "./color";
import { get_fake_value, seed_time } from "./fake";
import { sprintf } from "sprintf-js";
import { Text } from "./prm/text";
import { RadioButton, RADIO_SINGLE_RANDOM_VALUE, RADIO_SET_FIRST_VALUE } from "./prm/radio-button";
import {Action, TextLoading, VimeoIcon, VIMEO_ICON_TYPE} from "../client/funcs/utils";
import "./onboarding-tda/styles.scss";
import {OptionMenu} from "./prm/option-menu";
import {Slider} from "./prm/slider";
import { darkenColorString } from '../client/components/PerformanceCard/helpers';

const questionFontSize = "200%";
const TEXT_FONT_SIZE = "100%";

class FlowRender {

  /**
   * @param index: {Object} page ordering information
   * @param prmList: a list of parameter instances.
   */
  constructor(index, prmList=[]) {
    this.index = index;
    this.prmList = prmList;
  }

  /**
   * Initialize the {@param componentState}, and intialize it with any
   * {@param appState} that may exist. {@param component} is the React
   * component.
   */
  init(componentState, appState, component) {
    this.prmList.forEach(prm => {
      prm.initState(componentState);
      prm.loadFromState(appState, componentState);
    });
  };

  findPrm = id => {
    return this.prmList.find(prm => prm.name.id === id);
  };

  setDisabled = (state, disabled) => {
    this.prmList.forEach(prm => {
      prm.setDisabled(state, disabled);
    })
  }

  /**
   * Copies parameter data from component {@param state} into
   * an app state and returns it.
   * @returns app state.
   */
  saveToState = state => {
    let result = {};
    this.prmList.forEach(prm => {
      result = {
        ...result,
        ...(prm.saveToState(state)),
      }
    });
    return result;
  };

  /**
   * If a page wants to delete parameters after navigating away from the
   * page, we can specify them here. There are sometimes race conditions
   * so we can't write/delete onboarding parameters at the same time.
   */
  prmToDelete = state => {
    return [];
  }

  setRandomValues(state, component) {
    for(let i=0; i < this.prmList.length; i++) {
      const prm = this.prmList[i];

      if (!prm.fake || prm.getDisabled(state)) {
        continue;
      }

      if (prm.type === Text.type) {
        prm.setValue(state, get_fake_value(prm.fake));
      } else if (prm.type === RadioButton.type) {
        if (prm.fake === RADIO_SINGLE_RANDOM_VALUE) {
          prm.setRandomSingleValue(state);
        } else if (prm.fake === RADIO_SET_FIRST_VALUE) {
          const id = prm.buttonList[0].id.split("|")[1];

          prm.setValues(state, [id], true);
        }
      } else if (prm.type === OptionMenu.type) {
        prm.setRandomValue(state);
      } else if (prm.type === Slider.type) {
        prm.setRandomValue(state);
      }
    }
  };

  randomValues(component) {
    seed_time();
    let newState = Object.assign({}, component.state);
    this.setRandomValues(newState, component);
    newState.dirty = true;
    newState.highlight_rects = [];

    this.onChange(component, newState);

    component.setState(newState);
  };

  /**
   * This is the callback for changing the view state.
   * @param newState
   */
  onChange = (component, newState, prm) => {}

  /**
   * {@link #componentWillReceiveProps} callback.
   */
  onNewProps(component, newState, newProps) {}

  onMount(component, props, new_state) {}

  /**
   * Toggle Debug state.
   * @param component
   */
  toggleDebug(component) {}

  /**
   * Called when {@link #componentWillUnmount} is called.
   */
  onUnmount(component) {}

  /**
   * Returns the url of the next endpoint if the view is ready to
   * navigate to the next page. If its not, it highlights the
   * required parameters and returns null;
   *
   * @param component {React.Component}
   * @return {String} or null
   */
  canNavigateNext(component) {
    return false;
  }


  canNavigateBack(component) {
    return true;
  }

  /**
   * Called when this flow is finished and a new one is about
   * to be displayed.
   *
   * @param component: {OnboardingLayout}
   * @param new_state: {any}
   */
  onFinish(component, new_state) {}


  renderNextButton = (component, buttonStyle = {}, backButton, next_text="Next") => {
    let client = component.props.client;
    const buttonColor = getRGB(client.color.sign_on_button);
    const hoverColor = darkenColorString(getRGB(client.color.sign_on_button), 0.1);

    const handleOnMouseEnter = () => {
        if(backButton) {
            component.setState({
                backButtonIsHover: true
            });
        } else {
            component.setState({
                nextButtonIsHover: true
            })
        }
    }

    const handleOnMouseLeave = () => {
        if(backButton) {
            component.setState({
                backButtonIsHover: false
            })
        } else {
            component.setState({
                nextButtonIsHover: false
            })
        }
    }

    const getBgColor = () => {
        let bgColor = buttonColor
        if(backButton) {
            bgColor = component.state.backButtonIsHover ? hoverColor : buttonColor;
        } else {
            bgColor = component.state.nextButtonIsHover ? hoverColor : buttonColor;
        }

        return bgColor;
    }

    let style = {
      padding: "0.5rem",
      display: "inline-block",
    };

    let bStyle = {
      fontSize: TEXT_FONT_SIZE,
      fontFamily: "Poppins",
      fontWeight: "600",
      padding: "1rem 2rem",
      backgroundColor: getBgColor(),
      color: "#FFF",
      cursor: 'pointer',
      ...buttonStyle,
    };

    const iStyle = {
      paddingLeft: "0.75rem",
      paddingRight: "0.75rem",
    };
    const text = backButton ? "Back" : next_text;
    const cb = backButton
      ? () => component.navigateBack()
      : () => component.onNavigate();

    return (
      <div style={style}>
        <button
          type="button"
          style={bStyle}
          onClick={cb}
          onMouseEnter={handleOnMouseEnter}
          onMouseLeave={handleOnMouseLeave}
          >
          { backButton && <i className={"fa fa-arrow-left"} style={iStyle}/> }
          { text }
          { !backButton && <i className={"fa fa-arrow-right"} style={iStyle}/> }
        </button>
      </div>
    )
  };

  renderButton = (component, text, loading, cb) => {
    let client = component.props.client;
    const buttonColor = getRGB(client.color.sign_on_button);

    let style = {
      padding: "0.5rem",
      display: "inline-block",
    };

    let bStyle = {
      fontSize: TEXT_FONT_SIZE,
      fontFamily: "Poppins",
      fontWeight: "600",
      padding: "1rem 2rem",
      backgroundColor: buttonColor,
      color: "#FFF",
    };

    return (
      <div style={style}>
        <button
          type="button"
          style={bStyle}
          onClick={cb}>
          <TextLoading
            label={text}
            loading={loading}/>
        </button>
      </div>
    )
  }

  renderNextButtonRow = (component, do_back_button, next_text="Next") => {
    const container_style = {
      display: "flex",
      justifyContent: "center",
    }
    return this.renderSingleColumn( (
      <div style={container_style}>
        { do_back_button && this.renderNextButton(component, null, true) }
        { this.renderNextButton(component, {}, false, next_text) }
      </div>
    ));
  };

  renderActionButton = (text, cb, disabled= false, spinner=false) => {
    const div_style = {
      marginTop: "2rem",
    };
    const button_style = {
      padding: "0.5rem 3rem",
      fontWeight: "bold",
    };
    return (
      <div align={"center"} style={div_style}>
        <Action
          style={button_style}
          disabled={disabled}
          onClick={cb}>
          <TextLoading
            label={text}
            loading={spinner}/>
          { /* text */ }
        </Action>
      </div>
    )
  };

  renderSingleColumn = (contents, width="50%", customStyle={}, customSectionStyle={}) => {

    let style = {
      width: width,
      margin: "auto",
      ...customStyle
    };
    const section_style = {
      paddingTop: "1rem",
      paddingBottom: "0",
      ...customSectionStyle
    }

    const id = "onboarding-" + this.url;

    return (
      <section className="section" style={section_style} id={id}>
        <div className="single-column-responsive" style={style}>
          { contents }
        </div>
      </section>
    );
  };

  renderTitle = (component, text, width, vimeo_id, header_div=null) => {
    const container_style = {
      width,
      display: "flex",
      alignItems: "center",
      margin: "auto",
    }
    const title_style = {
      fontSize: "2.5rem",
      fontWeight: "800",
    }
    const section_style = {
      paddingTop: "1rem",
      paddingBottom: "0",
    }
    const video = vimeo_id
      ?
      (<VimeoIcon
          state={component.state}
          component={component}
          icon={VIMEO_ICON_TYPE.VIDEO_ICON}
          size={"5rem"}
          video_id={vimeo_id}
          id={"vimeo_" + vimeo_id}
        />) : null;

    return (
      <section className={"section"} style={section_style}>
        <div style={container_style}>
          <div style={title_style}>
            {text}
          </div>
          <div style={{ flex: "1"}}/>
          { header_div }
          <div>
            { video }
          </div>
        </div>
      </section>
    );
  }

  renderProgress = (component, width="50%") => {
    if (component.getOnboardingIndex() > 0) {
      //return null;
    }

    const container_style = {
      position: "relative",
      width: "100%",
      height: "1.5rem",
      backgroundColor: "#BBB",
      align: "left",
    };
    const client = component.props.client;
    const progress_width = (this.index.index + 1) / (this.index.total + 1) * 100;
    const bar_style = {
      backgroundColor: getRGB(client.color.sign_on_button),
      width: sprintf("%f%%", progress_width),
      height: "1.5rem",
      textAlign: "right",
    };
    const text_style = {
      position: "absolute",
      transform: "translate(0, -50%)",
      top: "50%",
      left: "0",
      color: "#FFF",
      paddingRight: "0.2rem",
      fontFamily: "Poppins",
      fontWeight: "600",
      paddingLeft: "1rem",
    };

    const caret_style = {
      color: getRGB(client.color.sign_on_button),
      position: "absolute",
      transform: "translate(0, -50%)",
      top: "50%",
      left: sprintf("%f%%", progress_width),
      fontSize: "2.2rem",
    }
    const progressBar = (
      <div style={container_style}>
        <i style={caret_style} className="fa fa-caret-right"/>
        <span style={text_style}>Progress</span>
        <div style={bar_style}/>
      </div>
    );
    return this.renderSingleColumn(progressBar, width);
  }

  onHighlightClick = component => () => {
    component.setState({
      highlight_rects: [],
    })
  }
  /**
   * Creates div overlays from component.state.highlight_rects
   * @param component {OnboardingLayout}
   */;
  renderHighlightRects = (component) => {
    if (component.state.highlight_rects.length === 0) {
      return [];
    }

    return component.state.highlight_rects.map((rect, index) => {
      const div_style = {
        position: "absolute",
        display: "block",
        background: "rgba(255, 0, 0, 0.3)",
        width: rect.x_max - rect.x_min,
        height: rect.y_max - rect.y_min,
        top: rect.y_min,
        left: rect.x_min,
        borderRadius: "0.5rem",
        boxShadow: "0 0 1rem 0 red",
      }

      return (
        <div key={index}>
          <div
            style={div_style}
            onClick={this.onHighlightClick(component)}>
          </div>
        </div>
      );
    });
  };

}

export { FlowRender, questionFontSize, TEXT_FONT_SIZE };
